// src/components/ClaimVoteModal.jsx
// Guest vote claim modal that hands off into the shared app auth flow.

import React from 'react';
import { X, Check } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { safeStorage } from '../utils/safeStorage';

export default function ClaimVoteModal({ onClose, onSuccess, trialTitle, voteSide, mode = 'post_vote' }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const isPreVote = mode === 'pre_vote';

  React.useEffect(() => {
    if (window.trackEvent) {
      window.trackEvent('claim_prompt_shown', { trial_title: trialTitle });
    }
  }, [trialTitle]);

  const handleContinueToAuth = async () => {
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        onSuccess?.();
        return;
      }

      if (window.trackEvent) {
        window.trackEvent('auth_handoff_started', {
          context: 'claim_vote',
          mode: isPreVote ? 'pre_vote' : 'post_vote',
          route: `${window.location.pathname}${window.location.search || ''}`,
          trial_title: trialTitle || null,
          vote_side: voteSide || null,
        });
      }

      const currentPath = `${window.location.pathname}${window.location.search || ''}`;
      safeStorage.set('gweffit_return_url', currentPath);

      const params = new URLSearchParams(window.location.search || '');
      params.set('auth', 'signup');
      params.set('auth_context', 'claim_vote');
      params.set(
        'auth_message',
        isPreVote
          ? 'Enter your email and we\'ll send a 6-digit code so you can cast your vote.'
          : 'Enter your email and we\'ll send a 6-digit code to keep your vote and keep going.'
      );
      window.location.href = `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
    } catch (err) {
      setError(err?.message || 'Could not open sign-in right now.');
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  const handleSkip = () => {
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && handleSkip()}
    >
      <div className="bg-slate-800 rounded-3xl p-6 max-w-md w-full relative border-2 border-green-500/50 max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-300"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-black text-white mb-2">
            {isPreVote ? 'Sign in to Cast Your Vote' : 'Vote counted. Keep it.'}
          </h2>
          <p className="text-slate-300 text-sm">
            {isPreVote ? (
              <>
                Sign in and we&apos;ll bring you right back to cast
                {voteSide ? <> <span className="font-bold text-purple-300">{voteSide}</span></> : null}
                {trialTitle && <> on &quot;{trialTitle}&quot;</>}
              </>
            ) : (
              <>
                You voted <span className="font-bold text-purple-300">{voteSide}</span>
                {trialTitle && <> on &quot;{trialTitle}&quot;</>}
                . Create your account to keep that vote and stay in the game.
              </>
            )}
          </p>
        </div>

        <div className="bg-slate-900/50 rounded-xl p-4 mb-6 border border-slate-700">
          <h3 className="font-bold text-white mb-2">
            {isPreVote ? '🔐 Sign in to continue' : 'Join GweffIT and keep going'}
          </h3>
          <ul className="text-sm text-slate-300 space-y-1">
            <li>✅ {isPreVote ? 'Return to this trial after sign-in' : 'Keep this vote tied to your profile'}</li>
            <li>✅ Save your rewards, streak, and progress</li>
            <li>✅ Follow creators and catch the final verdict</li>
            <li>✅ Join crews and shape more verdicts</li>
          </ul>
        </div>

        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 text-center mb-3">
          {isPreVote ? 'Continue in seconds' : 'Claim your vote in seconds'}
        </div>

        {error ? (
          <p className="text-sm text-red-400 mb-3 text-center">{error}</p>
        ) : null}

        <button
          type="button"
          onClick={handleContinueToAuth}
          disabled={loading}
          className={`w-full rounded-xl py-3 font-black text-sm mt-2 flex items-center justify-center gap-2 ${
            loading
              ? 'bg-slate-600 text-slate-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
          }`}
        >
          {loading ? 'Opening sign-in…' : 'Sign in or create account'}
        </button>

        <button
          type="button"
          onClick={handleSkip}
          className="w-full text-slate-400 text-sm hover:text-slate-300 mt-3"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
